import { useTranslation } from '@/lib/i18n';
import { useUserGradesQuery } from '../grades/hooks/useUserGradesQuery';
import { memberTypeLabel } from '../lib/userLabels';
import type { MemberType } from './useAdminUsersQuery';

/**
 * QA-414 — 사업자 회원은 회원 유형 옆에 현재 등급을 붙인다. 예: 사업자(1등급), 사업자(B2B).
 * 회원 상세 응답의 gradeId 를 등급 목록(등급 정보 탭과 같은 조회 — 캐시 공유)에서 이름으로 바꾼다.
 * 등급이 없거나 목록을 못 읽으면 종전처럼 "사업자"만.
 */
export const useMemberTypeDisplay = (memberType: MemberType | string | null | undefined, gradeId: string | null | undefined): string => {
  const { t } = useTranslation('common');
  const grades = useUserGradesQuery({ size: 50 });
  const base = memberTypeLabel(t, memberType);
  if (memberType !== 'BUSINESS' || !gradeId) return base;
  const name = grades.data?.content.find((g) => g.gradeId === gradeId)?.gradeName;
  return name ? `${base}(${name})` : base;
};

/**
 * 회원 상세 상단 요약 — 회원 유형 대신 등급을 보인다(개인·사업자 모두). 응답의 gradeName 을 먼저 쓰고, 없으면 gradeId 를
 * 등급 목록에서 이름으로 바꾼다. 등급을 모르면 종전처럼 회원 유형.
 */
export const useMemberGradeDisplay = (
  memberType: MemberType | string | null | undefined,
  gradeId: string | null | undefined,
  gradeName?: string | null,
): string => {
  const { t } = useTranslation('common');
  const grades = useUserGradesQuery({ size: 50 });
  const name = gradeName?.trim() || (gradeId ? grades.data?.content.find((g) => g.gradeId === gradeId)?.gradeName : undefined);
  return name || memberTypeLabel(t, memberType);
};
